import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const EMPTY = { name: "", phone: "", whatsapp: "", email: "", address: "", doc: "", notes: "" };

export default function Customers() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [costsOk, setCostsOk] = useState(true);

  const load = () => api.get("/customers").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/costs/pricing-context").then((r) => setCostsOk(!!r.data.configured)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/customers", form);
      toast.success("Cliente cadastrado!");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este cliente e seus equipamentos?")) return;
    await api.delete(`/customers/${id}`);
    toast.success("Cliente excluído.");
    load();
  };

  const filtered = items.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-5 animate-in-up" data-testid="customers-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Clientes</h1>
          <p className="text-sm text-slate-500">{items.length} cadastrados</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-12 bg-sky-600 hover:bg-sky-700 text-white" data-testid="new-customer-btn">
              <Plus className="h-4 w-4 mr-2" /> Novo cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{costsOk ? "Novo cliente" : "Configure seus custos primeiro"}</DialogTitle></DialogHeader>
            {!costsOk ? (
              <div className="space-y-4" data-testid="customers-costs-gate">
                <p className="text-sm text-slate-600">Preencha as informações básicas dos seus custos operacionais para que o GEPR possa calcular uma precificação adequada à realidade da sua empresa. O cadastro de clientes é liberado após a configuração mínima.</p>
                <Link to="/app/custos">
                  <Button type="button" className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" data-testid="customers-gate-cta">Preencher agora</Button>
                </Link>
                <p className="text-[11px] text-slate-400">Sua conta, perfil, configurações, suporte e navegação continuam disponíveis normalmente.</p>
              </div>
            ) : (
            <form onSubmit={save} className="space-y-3">
              <div className="space-y-1.5">
                <Label>Nome *</Label>
                <Input className="h-12" data-testid="customer-name" required value={form.name} onChange={set("name")} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Telefone</Label>
                  <Input className="h-12" data-testid="customer-phone" value={form.phone} onChange={set("phone")} />
                </div>
                <div className="space-y-1.5">
                  <Label>WhatsApp</Label>
                  <Input className="h-12" data-testid="customer-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>E-mail</Label>
                <Input className="h-12" data-testid="customer-email" type="email" value={form.email} onChange={set("email")} />
              </div>
              <div className="space-y-1.5">
                <Label>Endereço</Label>
                <Input className="h-12" data-testid="customer-address" value={form.address} onChange={set("address")} />
              </div>
              <div className="space-y-1.5">
                <Label>CNPJ/CPF (opcional)</Label>
                <Input className="h-12" data-testid="customer-doc" value={form.doc} onChange={set("doc")} />
              </div>
              <div className="space-y-1.5">
                <Label>Observações</Label>
                <Input className="h-12" data-testid="customer-notes" value={form.notes} onChange={set("notes")} />
              </div>
              <Button type="submit" className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" disabled={saving} data-testid="customer-save-btn">
                {saving ? "Salvando..." : "Salvar cliente"}
              </Button>
            </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
        <Input className="h-12 pl-9" placeholder="Buscar cliente..." data-testid="customer-search"
               value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className="grid gap-3">
        {filtered.length === 0 && (
          <Card className="border-slate-200"><CardContent className="p-8 text-center text-slate-500 text-sm">
            Nenhum cliente encontrado. Cadastre o primeiro!
          </CardContent></Card>
        )}
        {filtered.map((c) => (
          <Card key={c.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`customer-card-${c.id}`}>
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <Link to={`/app/clientes/${c.id}`} className="flex-1 min-w-0">
                <p className="font-semibold truncate">{c.name}</p>
                <p className="text-sm text-slate-500 truncate">{[c.phone, c.address].filter(Boolean).join(" · ") || "Sem contato"}</p>
              </Link>
              <div className="flex items-center gap-1">
                <Link to={`/app/clientes/${c.id}`}>
                  <Button variant="outline" className="h-11" data-testid={`customer-open-${c.id}`}>Abrir</Button>
                </Link>
                <Button variant="ghost" size="icon" className="h-11 w-11 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => remove(c.id)} data-testid={`customer-delete-${c.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
